// Guest vote migration — pure decision logic + sessionStorage safety net +
// an injectable orchestration of the post-submit "make an account" conversion.
//
// This module implements the corrected migration flow (plan C5 + C9). The
// orchestration (`migrateGuestVote`) is pure with respect to its injected
// `services` so the Firestore-call SEQUENCE and every failure path can be
// unit-tested without Firebase. SchedulerPage wires real Firebase/data ops into
// those services.

// ---------------------------------------------------------------------------
// sessionStorage safety net
// ---------------------------------------------------------------------------

export function stashKey(schedulerId) {
  return `pendingGuestVoteMigration:${schedulerId}`;
}

function safeSessionStorage() {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  return window.sessionStorage;
}

// Persist the guest's current selections BEFORE the Google popup opens, so a
// crash / reload mid-conversion can always recover. `guestName` is captured so
// the anon docs can be re-created verbatim if a sign-in fails (C9 step 2c).
export function stashGuestVote({ schedulerId, guestName, schedulerVotes, embeddedVotes }) {
  const store = safeSessionStorage();
  if (!store || !schedulerId) return;
  const payload = {
    guestName: guestName || "",
    schedulerVotes: schedulerVotes || { votes: {}, noTimesWork: false },
    embeddedVotes: Array.isArray(embeddedVotes) ? embeddedVotes : [],
    stashedAt: Date.now(),
  };
  try {
    store.setItem(stashKey(schedulerId), JSON.stringify(payload));
  } catch {
    // Quota / serialization — best-effort safety net; ignore.
  }
}

// Read the stash WITHOUT removing it. The stash is a persistent safety net and
// must survive a read so a failed sign-in can still restore the anon docs and a
// retry / reload can still recover. Use `clearGuestVote` to remove after success.
export function popGuestVote(schedulerId) {
  const store = safeSessionStorage();
  if (!store || !schedulerId) return null;
  try {
    const raw = store.getItem(stashKey(schedulerId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearGuestVote(schedulerId) {
  const store = safeSessionStorage();
  if (!store || !schedulerId) return;
  try {
    store.removeItem(stashKey(schedulerId));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Pure decision logic
// ---------------------------------------------------------------------------

function normalizeSelection(s) {
  return {
    votes: s && s.votes && typeof s.votes === "object" ? s.votes : {},
    noTimesWork: Boolean(s && s.noTimesWork),
  };
}

export function areSchedulerSelectionsEqual(a, b) {
  const na = normalizeSelection(a);
  const nb = normalizeSelection(b);
  const ka = Object.keys(na.votes).sort();
  const kb = Object.keys(nb.votes).sort();
  if (ka.length !== kb.length) return false;
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] !== kb[i]) return false;
    if (na.votes[ka[i]] !== nb.votes[ka[i]]) return false;
  }
  return na.noTimesWork === nb.noTimesWork;
}

// Decide how to migrate a stashed guest selection onto a real account:
//   - no existing vote        => "write"     (write the stashed selection)
//   - existing + identical    => "preserve"  (no-op, keep existing)
//   - existing + different    => "conflict"  (caller prompts; user picks)
export function resolveGuestVoteMigration({ existingVote, stashedSelections } = {}) {
  if (!existingVote) return { kind: "write" };
  if (areSchedulerSelectionsEqual(existingVote, stashedSelections)) return { kind: "preserve" };
  return { kind: "conflict" };
}

function hasEmbeddedSelection(vote) {
  if (!vote) return false;
  if (Array.isArray(vote.rankings) && vote.rankings.length > 0) return true;
  if (Array.isArray(vote.optionIds) && vote.optionIds.length > 0) return true;
  if (typeof vote.otherText === "string" && vote.otherText.trim().length > 0) return true;
  return false;
}

// Build the stashed payload from the live guest scheduler vote + the guest's
// embedded poll votes. Captures SELECTIONS only — never identity fields or the
// `isGuest`/`guestName` flags (those are re-derived from the account on write).
export function buildGuestVoteStash({ guestName, schedulerVote, embeddedVotes }) {
  const sv = schedulerVote || {};
  return {
    guestName: guestName || sv.guestName || "",
    schedulerVotes: {
      votes: sv.votes && typeof sv.votes === "object" ? sv.votes : {},
      noTimesWork: Boolean(sv.noTimesWork),
    },
    embeddedVotes: (Array.isArray(embeddedVotes) ? embeddedVotes : [])
      .filter(({ vote }) => hasEmbeddedSelection(vote))
      .map(({ pollId, vote }) => {
        const entry = { pollId };
        if (Array.isArray(vote.rankings)) {
          entry.rankings = vote.rankings;
        } else {
          entry.optionIds = Array.isArray(vote.optionIds) ? vote.optionIds : [];
        }
        if (typeof vote.otherText === "string" && vote.otherText.length > 0) {
          entry.otherText = vote.otherText;
        }
        return entry;
      }),
  };
}

// ---------------------------------------------------------------------------
// Orchestration (C5 + C9). All side effects are injected via `services`.
// ---------------------------------------------------------------------------

const POPUP_CANCEL_CODES = new Set(["auth/popup-closed-by-user", "auth/cancelled-popup-request"]);

function identityFromUser(user) {
  return { email: user?.email || null, photoURL: user?.photoURL || null };
}

// Case 1 — link succeeded: uid unchanged, now non-anonymous. Join (if not
// already), rewrite the doc as a normal participant vote, clear the stash.
async function migrateInPlace(user, { stashed, services }) {
  const uid = user.uid;
  let joined = false;
  try {
    const already = await services.isParticipant(uid);
    if (!already) {
      await services.join(uid);
      joined = true;
    }
  } catch (error) {
    return { outcome: "error", error, stage: "join" };
  }
  try {
    await services.writeParticipantVote(uid, identityFromUser(user), stashed);
  } catch (error) {
    // C9: join succeeded but the rewrite failed. Self-leave so we never leave a
    // counted-but-unvoted participant (which would pin votesAllSubmitted
    // stale-true). Keep the stash; the user can retry.
    if (joined) {
      try {
        await services.leave(uid);
      } catch {
        // best-effort compensation
      }
    }
    return { outcome: "error", error, stage: "rewrite-after-join", selfLeft: joined };
  }
  await services.clearStash();
  return { outcome: "linked", user };
}

// Case 2/3 — the Google account already exists as a separate user. We are still
// anonymous in the catch, so delete the orphan anon docs, sign in as the
// existing user, then write/preserve/replace.
async function migrateToExistingAccount(err, { anonUid, stashed, services, promptConflict }) {
  // C9 step 2a: VALIDATE the credential BEFORE any destructive operation.
  const credential = await services.credentialFromError(err);
  if (!credential) {
    return { outcome: "error", error: err, stage: "credential" };
  }

  // C9 step 2b: still anonymous here — delete the orphan anon docs. The guest
  // self-delete rule (C2) permits this on a link-shared OPEN scheduler.
  try {
    await services.deleteAnonDocs(anonUid, stashed);
  } catch (error) {
    // anon docs still intact; stash retained. Surface and stop.
    return { outcome: "error", error, stage: "delete-anon" };
  }

  // C9 step 2c: sign in as the existing user. If this throws while we are still
  // anonymous, RESTORE the anon docs from the stash (we are still
  // isRequester(anonUid), so the guest write rule allows the re-create).
  let existingUser;
  try {
    existingUser = await services.signInWithCredential(credential);
  } catch (error) {
    try {
      await services.restoreAnonDocs(anonUid, stashed);
    } catch {
      // best-effort restore
    }
    return { outcome: "error", error, stage: "signin", restored: true };
  }

  return finalizeExistingAccount(existingUser, { stashed, services, promptConflict });
}

// Shared finalization once signed in as the existing (real) user. Used by both
// the nudge-click path and load-time recovery.
async function finalizeExistingAccount(user, { stashed, services, promptConflict }) {
  const uid = user.uid;
  let joined = false;
  try {
    const already = await services.isParticipant(uid);
    if (!already) {
      await services.join(uid);
      joined = true;
    }
  } catch (error) {
    return { outcome: "error", error, stage: "join-existing" };
  }

  let existingVote;
  try {
    existingVote = await services.readExistingVote(uid);
  } catch (error) {
    // C9: read failed after a join — self-leave to avoid stale-true, keep stash.
    if (joined) {
      try {
        await services.leave(uid);
      } catch {
        // best-effort
      }
    }
    return { outcome: "error", error, stage: "read-existing", selfLeft: joined };
  }

  // If the existing doc is our OWN un-migrated guest doc (same uid — e.g.
  // load-time recovery of a Case-1 link that reloaded before the rewrite), ALWAYS
  // rewrite it as a participant vote. Never "preserve": that would leave
  // isGuest/guestName on a real account's vote, so completion ignores it and
  // normal edits are denied.
  const decision =
    existingVote?.isGuest === true
      ? { kind: "write" }
      : resolveGuestVoteMigration({
          existingVote,
          stashedSelections: stashed.schedulerVotes,
        });

  if (decision.kind === "preserve") {
    // Identical selections, or the user chose to keep the previous vote: leave
    // the existing vote, discard the stash.
    await services.clearStash();
    return { outcome: "preserved", user };
  }

  if (decision.kind === "write") {
    try {
      await services.writeParticipantVote(uid, identityFromUser(user), stashed);
    } catch (error) {
      if (joined) {
        try {
          await services.leave(uid);
        } catch {
          // best-effort
        }
      }
      return { outcome: "error", error, stage: "write-after-join", selfLeft: joined };
    }
    await services.clearStash();
    return { outcome: "migrated", user };
  }

  // decision.kind === "conflict" — surface the modal. Escape/backdrop maps to
  // "keep previous" (preserve) in the SchedulerPage promptConflict wiring, so a
  // dismiss can never silently abandon the existing vote (C9).
  let choice;
  try {
    choice = await promptConflict(existingVote, stashed);
  } catch {
    choice = "preserve";
  }
  if (choice !== "replace") {
    await services.clearStash();
    return { outcome: "preserved", user };
  }
  try {
    await services.writeParticipantVote(uid, identityFromUser(user), stashed);
  } catch (error) {
    // Replace failed — the existing vote is intact, so no self-leave needed.
    return { outcome: "error", error, stage: "replace-existing" };
  }
  await services.clearStash();
  return { outcome: "replaced", user };
}

// The top-level conversion handler. The caller MUST `stashGuestVote(...)` to
// sessionStorage BEFORE invoking this (the stash is the safety net for a popup
// crash). See plan C5.
export async function migrateGuestVote({
  schedulerId,
  anonUid,
  stashed,
  services,
  promptConflict,
}) {
  let linked;
  try {
    linked = await services.linkGoogleAccount(); // { user, credential }
  } catch (err) {
    if (err?.code === "auth/credential-already-in-use") {
      return migrateToExistingAccount(err, { anonUid, stashed, services, promptConflict });
    }
    if (POPUP_CANCEL_CODES.has(err?.code)) {
      // Do NOTHING — the guest vote doc + stash stay intact. We never delete the
      // anon doc before the popup resolves, so a cancel cannot lose a vote.
      return { outcome: "cancelled" };
    }
    return { outcome: "error", error: err, stage: "link" };
  }

  // After linkWithPopup the SDK user is non-anonymous, but the session's
  // sign_in_provider claim stays "anonymous" until a fresh credential sign-in —
  // so the rules (which gate join + participant vote writes on
  // !isAnonymousProvider()) would still DENY. Re-sign-in with the linked Google
  // credential to re-establish the session as google.com (uid unchanged) before
  // any participant op. The guest vote doc is untouched here, so a failure loses
  // nothing (stash also intact).
  let user;
  try {
    user = await services.signInWithCredential(linked.credential);
  } catch (err) {
    return { outcome: "error", error: err, stage: "resignin" };
  }

  return migrateInPlace(user, { schedulerId, stashed, services });
}

// Load-time recovery — invoked from SchedulerPage when a non-anonymous user
// mounts with a pending stash (e.g. they converted and the page reloaded before
// the migration finished). Runs the best-effort finalization and cleans up.
export async function resumeGuestVoteMigration({ user, stashed, services, promptConflict }) {
  return finalizeExistingAccount(user, { stashed, services, promptConflict });
}
